import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

const topBrands = [
  { rank: 1, name: "華為 (Huawei)", industry: "科技通信", score: 96.8, change: "↑ 1", country: "中國" },
  { rank: 2, name: "抖音 / TikTok", industry: "社交媒體", score: 95.2, change: "↑ 3", country: "中國" },
  { rank: 3, name: "聯想 (Lenovo)", industry: "電子科技", score: 93.5, change: "—", country: "中國" },
  { rank: 4, name: "阿里巴巴 (Alibaba)", industry: "電子商務", score: 92.1, change: "↓ 2", country: "中國" },
  { rank: 5, name: "大疆 (DJI)", industry: "無人機科技", score: 90.8, change: "↑ 5", country: "中國" },
  { rank: 6, name: "海爾 (Haier)", industry: "家電製造", score: 89.4, change: "↑ 1", country: "中國" },
  { rank: 7, name: "小米 (Xiaomi)", industry: "消費電子", score: 88.7, change: "↓ 1", country: "中國" },
  { rank: 8, name: "比亞迪 (BYD)", industry: "新能源汽車", score: 87.3, change: "↑ 12", country: "中國" },
  { rank: 9, name: "騰訊 (Tencent)", industry: "互聯網科技", score: 86.9, change: "↓ 2", country: "中國" },
  { rank: 10, name: "安踏 (ANTA)", industry: "體育用品", score: 85.5, change: "↑ 8", country: "中國" },
  { rank: 11, name: "中國中車 (CRRC)", industry: "軌道交通", score: 84.2, change: "↑ 3", country: "中國" },
  { rank: 12, name: "中興通訊 (ZTE)", industry: "通信設備", score: 83.6, change: "↓ 4", country: "中國" },
  { rank: 13, name: "格力 (Gree)", industry: "空調製造", score: 82.9, change: "↓ 1", country: "中國" },
  { rank: 14, name: "美的 (Midea)", industry: "家電製造", score: 82.1, change: "—", country: "中國" },
  { rank: 15, name: "京東方 (BOE)", industry: "顯示技術", score: 81.4, change: "↑ 6", country: "中國" },
  { rank: 16, name: "寧德時代 (CATL)", industry: "新能源電池", score: 80.8, change: "↑ 15", country: "中國" },
  { rank: 17, name: "OPPO", industry: "消費電子", score: 79.5, change: "↓ 2", country: "中國" },
  { rank: 18, name: "三一重工 (SANY)", industry: "工程機械", score: 78.9, change: "↑ 4", country: "中國" },
  { rank: 19, name: "海康威視 (Hikvision)", industry: "安防科技", score: 78.2, change: "↓ 3", country: "中國" },
  { rank: 20, name: "吉利汽車 (Geely)", industry: "汽車製造", score: 77.6, change: "↑ 7", country: "中國" },
];

export default function Rankings2025Top100() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNav = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(href);
  };

  return (
    <PageShell>
      <section className="relative pt-24 pb-20 bg-gradient-to-b from-accent-800 to-accent-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Luxurious%20golden%20trophy%20and%20laurel%20wreath%20pattern%20with%20elegant%20geometric%20accents%2C%20dark%20gold%20background%2C%20prestigious%20award%20ceremony%20aesthetic%2C%20warm%20sophisticated%20lighting%2C%20no%20text%2C%20no%20people&width=1600&height=600&seq=ranking-hero-01&orientation=landscape')] bg-cover bg-center opacity-20"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("nav.rankings") }, { label: t("rankings.top100.title") }]} />
          <div className="mt-6 max-w-3xl">
            <div className="inline-block bg-accent-400/30 text-background-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4 whitespace-nowrap">
              {t("rankings.top100.badge")}
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("rankings.top100.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("rankings.top100.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-12 bg-background-100 border-b border-background-200">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: "ri-bar-chart-2-line", key: "market", weight: "30%" },
              { icon: "ri-global-line", key: "cognition", weight: "25%" },
              { icon: "ri-medal-line", key: "innovation", weight: "25%" },
              { icon: "ri-heart-line", key: "esg", weight: "20%" },
            ].map((item, i) => (
              <div key={i} className="bg-background-50 rounded-lg p-5 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-3">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{t(`rankings.top100.method.${item.key}`)}</h3>
                <p className="text-xs text-foreground-500 mb-2">{t(`rankings.top100.method.${item.key}.desc`)}</p>
                <span className="text-sm font-bold text-primary-600">{t("weight")}: {item.weight}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ranking Table */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">{t("rankings.top100.table.title")}</h2>
          <p className="text-foreground-500 mb-8">{t("rankings.top100.table.desc")}</p>

          <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-primary-800 text-background-50 text-sm">
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.rank")}</th>
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.name")}</th>
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.industry")}</th>
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.score")}</th>
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.change")}</th>
                    <th className="text-left px-5 py-3.5 font-medium whitespace-nowrap">{t("rankings.top100.table.country")}</th>
                  </tr>
                </thead>
                <tbody>
                  {topBrands.map((brand) => {
                    const isTop3 = brand.rank <= 3;
                    return (
                      <tr key={brand.rank} className={`border-b border-background-200 hover:bg-background-100 transition-colors ${isTop3 ? "bg-accent-50/30" : ""}`}>
                        <td className="px-5 py-4">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                            brand.rank === 1 ? "bg-accent-500 text-background-50" :
                            brand.rank === 2 ? "bg-foreground-300 text-background-50" :
                            brand.rank === 3 ? "bg-accent-600 text-background-50" :
                            "bg-background-100 text-foreground-600"
                          }`}>
                            {brand.rank}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-heading font-semibold text-foreground-900 whitespace-nowrap">{brand.name}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-foreground-700 bg-background-100 px-2.5 py-1 rounded whitespace-nowrap">{brand.industry}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-background-200 rounded-full overflow-hidden">
                              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${brand.score}%` }}></div>
                            </div>
                            <span className="text-sm font-semibold text-foreground-900">{brand.score}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-sm font-medium ${
                            brand.change.startsWith("↑") ? "text-secondary-600" :
                            brand.change === "—" ? "text-foreground-400" :
                            "text-red-500"
                          }`}>
                            {brand.change}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-foreground-600">{brand.country}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-foreground-400 mt-4 text-center">
            {t("rankings.top100.footer")}
          </p>
        </div>
      </section>

      {/* Other Rankings Reports */}
      <section className="py-16 md:py-20 bg-background-50 border-t border-background-200">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            {t("rankings.top100.more.title")}
          </h2>
          <p className="text-foreground-500 mt-2 max-w-xl mx-auto">
            {t("rankings.top100.more.desc")}
          </p>
        </div>

        {[
          {
            titleKey: "rankings.top100.reports.0.title",
            descKey: "rankings.top100.reports.0.desc",
            link: "https://www.chinanationalbrand.org.cn/article/news/detail/id/42682",
            bg: "bg-background-50",
            image: "https://readdy.ai/api/search-image?query=Lush%20golden%20rice%20paddies%20during%20warm%20sunset%20harvest%20season%20with%20elegant%20composition%20and%20soft%20natural%20lighting%2C%20warm%20amber%20and%20cream%20tones%2C%20misty%20rural%20agricultural%20landscape%2C%20premium%20editorial%20photography%2C%20no%20text%2C%20no%20people&width=1200&height=500&seq=ranking-rice-01&orientation=landscape",
            icon: "ri-seedling-line",
            iconBg: "bg-accent-100 text-accent-700",
          },
          {
            titleKey: "rankings.top100.reports.1.title",
            descKey: "rankings.top100.reports.1.desc",
            link: "https://www.chinanationalbrand.org.cn/article/news/detail/action/news/id/42672",
            bg: "bg-background-100",
            image: "https://readdy.ai/api/search-image?query=Elegant%20luxury%20fabric%20rolls%20and%20silk%20threads%20in%20warm%20neutral%20tones%20on%20polished%20surface%2C%20artisan%20textile%20craftsmanship%20aesthetic%2C%20soft%20studio%20lighting%2C%20cream%20and%20gold%20palette%2C%20sophisticated%20editorial%20fashion%20industry%20photography%2C%20no%20text%2C%20no%20people&width=1200&height=500&seq=ranking-textile-01&orientation=landscape",
            icon: "ri-shirt-line",
            iconBg: "bg-secondary-100 text-secondary-700",
          },
          {
            titleKey: "rankings.top100.reports.2.title",
            descKey: "rankings.top100.reports.2.desc",
            link: "https://www.chinanationalbrand.org.cn/article/news/detail/id/36612",
            bg: "bg-background-50",
            image: "https://readdy.ai/api/search-image?query=Refined%20amber%20whiskey%20bottles%20arranged%20on%20dark%20mahogany%20wood%20surface%2C%20warm%20golden%20glow%20lighting%2C%20luxury%20spirits%20still%20life%20photography%20with%20elegant%20glass%20reflections%2C%20dark%20sophisticated%20background%20with%20burgundy%20and%20gold%20accents%2C%20premium%20branding%20aesthetic%2C%20no%20text%2C%20no%20people&width=1200&height=500&seq=ranking-spirits-01&orientation=landscape",
            icon: "ri-cup-line",
            iconBg: "bg-accent-100 text-accent-700",
          },
          {
            titleKey: "rankings.top100.reports.3.title",
            descKey: "rankings.top100.reports.3.desc",
            link: "https://www.toutiao.com/article/7286771304265482809/?upstream_biz=doubao&source=m_redirect",
            bg: "bg-background-100",
            image: "https://readdy.ai/api/search-image?query=Serene%20vineyard%20rows%20stretching%20across%20rolling%20hills%20at%20golden%20hour%20sunset%2C%20warm%20atmospheric%20light%20with%20rich%20green%20and%20amber%20tones%2C%20elegant%20wine%20region%20landscape%20photography%2C%20sophisticated%20editorial%20style%20with%20soft%20bokeh%2C%20premium%20terroir%20aesthetic%2C%20no%20text%2C%20no%20people&width=1200&height=500&seq=ranking-wine-01&orientation=landscape",
            icon: "ri-drop-line",
            iconBg: "bg-secondary-100 text-secondary-700",
          },
        ].map((report, i) => (
          <section key={i} className={`py-12 md:py-16 ${report.bg}`}>
            <div className="w-full max-w-4xl mx-auto px-4 md:px-8 text-center">
              <div className="w-full h-[200px] md:h-[280px] rounded-lg overflow-hidden mb-6">
                <img
                  src={report.image}
                  alt={t(report.titleKey)}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${report.iconBg}`}>
                  <i className={`${report.icon} text-lg`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 leading-relaxed">
                  {t(report.titleKey)}
                </h3>
              </div>
              <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                {t(report.descKey).split("\n\n").map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
              <div className="mt-6">
                <a
                  href={report.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary-600 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t("rankings.top100.reports.learnMore")}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </section>
        ))}
      </section>

      {/* CTA */}
      <section className="py-12 bg-accent-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-3">{t("rankings.top100.cta.title")}</h2>
          <p className="text-sm text-foreground-600 mb-5 max-w-lg mx-auto">
            {t("rankings.top100.cta.desc")}
          </p>
          <a href="/contact" onClick={handleNav("/contact")} className="inline-flex items-center gap-2 bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            {t("rankings.top100.cta.btn")} <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>
      </section>
    </PageShell>
  );
}